import { type FC, type ReactNode, useEffect, useState } from "react";
import { AppBar, Box, Divider, Tab, Tabs } from "@mui/material";
import { useTranslation } from "@/shared/hooks";

const SIDEBAR_WIDTH = 400;

export interface ContentLayoutTab {
  value: string;
  label: string;
  content: ReactNode;
}

interface ContentLayoutProps {
  children: ReactNode;
  // Обратная совместимость: один таб "Похожие видео" (VideoScreen — без
  // изменений). Новые вызовы с несколькими табами (StreamScreen — Task 16)
  // используют `tabs` напрямую.
  sidebar?: ReactNode;
  tabs?: ContentLayoutTab[];
}

// Порт референсных ContentLayout + ContentDesktopSidebar + Tabs: основная
// колонка на всю ширину, справа фиксированный сайдбар 400px (desktop md+)
// с таб-баром и собственным скроллом. Таб-бар скрывается только в точном
// референсном случае — единственный активный таб это CHAT (см. Chat.js);
// одиночный FEATURED_VIDEO-таб (VideoScreen) бар по-прежнему показывает.
export const ContentLayout: FC<ContentLayoutProps> = ({
  children,
  sidebar,
  tabs,
}) => {
  const { t } = useTranslation();

  const resolvedTabs: ContentLayoutTab[] =
    tabs ??
    (sidebar
      ? [
          {
            value: "FEATURED_VIDEO",
            label: t("video.similar.title"),
            content: sidebar,
          },
        ]
      : []);

  const [activeTab, setActiveTab] = useState(resolvedTabs[0]?.value ?? "");

  // Табы могут появляться/исчезать после загрузки данных (например CHAT —
  // только когда stream.isChatEnabled становится известен) — если текущий
  // активный таб больше не существует, переключаемся на первый доступный.
  const tabValues = resolvedTabs.map((tab) => tab.value).join(",");
  useEffect(() => {
    const firstTab = resolvedTabs[0];
    if (firstTab && !resolvedTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(firstTab.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValues]);

  const shouldHideTabBar =
    resolvedTabs.length === 1 && resolvedTabs[0]?.value === "CHAT";

  const activeContent = resolvedTabs.find(
    (tab) => tab.value === activeTab,
  )?.content;

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ width: "100%" }}>{children}</Box>

      {resolvedTabs.length > 0 && (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            position: "relative",
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              position: "fixed",
              // Совпадает с офсетом шапки (и top route-диалога).
              top: { xs: 56, sm: 64 },
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!shouldHideTabBar && (
              <>
                <AppBar elevation={0} color="default" position="static">
                  <Tabs
                    value={activeTab}
                    onChange={(_event, value: string) => setActiveTab(value)}
                    indicatorColor="primary"
                    variant="fullWidth"
                  >
                    {resolvedTabs.map((tab) => (
                      <Tab
                        key={tab.value}
                        label={tab.label}
                        value={tab.value}
                      />
                    ))}
                  </Tabs>
                </AppBar>
                <Divider />
              </>
            )}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                scrollbarGutter: "stable",
              }}
            >
              {activeContent}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
